// src/CcpAdminUsers/Lists/Lists.js
import React, { Fragment, useEffect, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import GridToolbarNoExport from "../../common/GridToolbarNoExport";
import AppDataGrid from "../../common/AppDataGrid";
import {
  IconButton,
  Snackbar,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  FieldPath,
  updateDoc,
  doc,
} from "firebase/firestore";
import {
  db,
  deleteUser,
  massUpdateCcpSchoolFlag,
} from "../../../config/firebase";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";

const Lists = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleMassUpdate = async () => {
    if (selectedUserIds.length === 0) {
      setSnackbarSeverity("warning");
      setSnackbarMessage("No users selected for update.");
      setSnackbarOpen(true);
      return; // Exit if no users are selected
    }
    setUpdating(true);

    try {
      // Initialize an array to store the promises
      const updatePromises = [];

      for (const userId of selectedUserIds) {
        // Update Firestore document using the correct FieldPath

        updatePromises.push(
          updateDoc(doc(db, "users", userId), { ccpSchoolFlag: false }),
        );
      }

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      setSnackbarSeverity("success");
      setSnackbarMessage("Mass update successful!");
      fetchUsers(); // Refetch users to update the grid
      setSelectedUserIds([]); // Clear selections
      setSelectAll(false); // Uncheck Select All
    } catch (error) {
      console.error("Error during mass update:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Mass update failed. Check console for details.");
    } finally {
      setUpdating(false);
      setSnackbarOpen(true);
    }
  };

  const handleRowSelection = (ids) => {
    const selectedIds = new Set(ids);
    setSelectedUserIds(Array.from(selectedIds)); // Store as an array

    const displayedUserIds = filteredUsers.map((user) => user.id);
    setSelectAll(selectedIds.size === displayedUserIds.length);
  };

  const handleSelectAllClick = (checked) => {
    setSelectAll(checked);

    if (checked) {
      const displayedUserIds = filteredUsers.map((user) => user.id);
      setSelectedUserIds(displayedUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Query for users where ccpSchoolFlag is null (or missing)
      const q = query(
        collection(db, "users"),
        where("ccpDistrictFlag", "==", true),
      );

      const querySnapshot = await getDocs(q);
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to fetch users. Check console for details.");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const searchValue = searchText.toLowerCase();

    return (
      user.district.toLowerCase().includes(searchValue) ||
      user.name.toLowerCase().includes(searchValue) ||
      user.email.toLowerCase().includes(searchValue) ||
      user.phone.toLowerCase().includes(searchValue) ||
      user.title.toLowerCase().includes(searchValue) ||
      user.userType.toLowerCase().includes(searchValue) ||
      String(user.school_id).toLowerCase().includes(searchValue) ||
      user.id.toLowerCase().includes(searchValue)
    );
  });

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleAddOpen = () => {
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditSuccess = () => {
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
    handleEditClose();
    // Fetch updated users
    fetchUsers();
  };

  const handleAddSuccess = () => {
    setSnackbarMessage("New user has been created successfully");
    setSnackbarOpen(true);
    handleAddClose();
    // Fetch updated users
    fetchUsers();
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true); // Set deleting to true
    try {
      // Perform deletion from Firebase Auth and Firestore
      const result = await deleteUser(selectedUser.id); // Assuming id is the user's document ID

      if (result.success) {
        setSnackbarSeverity("success");
        setSnackbarMessage("User deleted successfully");
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to delete user");
    } finally {
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
      setDeleting(false); // Set deleting to false
      // Fetch updated users
      fetchUsers();
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const columns = [
    { field: "district", headerName: "School", flex: 1 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "title", headerName: "Title", flex: 1 },
    { field: "userType", headerName: "User Type", flex: 1 },
    { field: "ccpSchoolFlag", headerName: "CCP School", flex: 1 }, // New
    { field: "ccpAdminFlag", headerName: "CCP Admin", flex: 1 }, // New
    { field: "ccpDistrictFlag", headerName: "CCP District", flex: 1 }, // New
    { field: "techPrepAdminFlag", headerName: "Tech Prep Admin", flex: 1 },
    { field: "school_id", headerName: "School", flex: 1 },

    { field: "id", headerName: "id", flex: 1 },
    {
      field: "select",
      headerName: "Select",
      width: 80, // Adjust width as needed
      sortable: false, // Make unsortable
      filterable: false, // Remove filtering
      hideable: false, // Prevent hiding
      renderCell: (params) => (
        <Checkbox
          checked={selectedUserIds.includes(params.id)}
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked) {
              setSelectedUserIds([...selectedUserIds, params.id]);
            } else {
              setSelectedUserIds(
                selectedUserIds.filter((id) => id !== params.id),
              );
            }
          }}
        />
      ),
    },
  ];

  return (
    <Fragment>
      <Box
        display="flex"
        alignItems="center"
        style={{ marginBottom: "1rem", marginTop: "1rem" }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={(e) => handleSelectAllClick(e.target.checked)}
            />
          }
          label="Select All"
        />
      </Box>

      <TextField
        label="Filter"
        variant="outlined"
        value={searchText}
        onChange={handleSearchChange}
        fullWidth
        margin="normal" // Add margin
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon /> {/* Replace with actual search icon component */}
            </InputAdornment>
          ),
        }}
        style={{ marginBottom: "1rem", marginTop: "1rem" }} // Add marginBottom for spacing
      />
      {/* Mass Update Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleMassUpdate}
        style={{ marginBottom: 16, marginTop: 10, marginRight: "1rem" }}
      >
        Mass Update
      </Button>
      {updating && ( // Show spinner when updating
        <CircularProgress size={24} style={{ marginLeft: 8 }} />
      )}
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddOpen}
        style={{ marginBottom: 16, marginTop: 10 }}
      >
        Add New User
      </Button>
      {/* Mass Update Button  <Button
        variant="contained"
        color="primary"
        onClick={handleMassUpdate}
        style={{ marginBottom: 16, marginTop: 10 }} // Add styling as needed
      >
        Mass Update
      </Button>*/}
      <Paper mt={2} mb={2} ml={2} mr={2}>
        <AppDataGrid
          sx={{ p: 3 }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns status and traderName, the other columns will remain visible
                id: false,
              },
            },
          }}
          checkboxSelection={false} // Disable built-in checkbox selection
          onRowSelectionModelChange={handleRowSelection}
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbarNoExport }}
          autosizeOnMount
          autoHeight
          pagination
          pageSize={25}
          pageSizeOptions={[25]}
          headerClassName="custom-header-class"
        />
      </Paper>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
        message="Are you sure you want to delete this user?"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default Lists;
